import cloneDeep from "lodash/cloneDeep";
import { getDiscountProviderProfile } from "./providers";
import type { ApplyPlan, DiscountOffer, Sale, VerifyOutcome } from "./types";

/**
 * The partner conversation: what to SEND, and what the answer MEANS.
 *
 * 🔴 **Every per-partner difference in this file is read from a capability profile.** That is
 * the point of the file. The two functions it replaces —`sendApplyRequest` and
 * `externalDiscountOtpValidate` — carried nine `isExternalProviderC1/C2/C1C2` calls between
 * them, and because those predicates overlap (`C1C2 ⊃ C1`) no single site told you which
 * partners reached it. A new partner meant reading all nine and guessing.
 *
 * Pure: request builders take arguments and return bodies; classifiers take a response and
 * return a named outcome. Nothing here dispatches — `./index` does that.
 *
 * ## The conversation
 *
 * ```
 *   apply  ──▶ getApplyPlan(response, offer)
 *                ├── "apply-now"  ──▶ write the discount onto plan.sale
 *                ├── "needs-otp"  ──▶ open the dialog, then:
 *                │      verify ──▶ classifyVerifyResponse(response, metaId)
 *                │                   ├── "verified" ──▶ write onto outcome.sale
 *                │                   └── "failed"   ──▶ report outcome.message
 *                └── "failed"     ──▶ report plan.code
 * ```
 */

/* ── applying ──────────────────────────────────────────────────────────────────────────── */

/** The `redeemPoints` body that asks a partner to apply one of its offers. */
export interface ApplyRequest {
  loyaltyInput: { discountCode?: string; couponCode?: string };
  providerId?: string;
  metaId?: string;
  sale: Sale;
}

/**
 * The apply request for a partner offer.
 *
 * ⚠️ `couponCode` prefers the one the COUPON API attributed to a typed code over the offer's
 * own. A cashier who types a partner code gets an `appliedDiscountInfo.couponCode` that is the
 * customer-specific instance of a campaign; the offer record carries the campaign's.
 */
export const buildApplyRequest = (
  offer: DiscountOffer,
  {
    sale,
    providerId,
    metaId,
    appliedDiscountInfo = {},
  }: {
    sale: Sale;
    providerId?: string;
    metaId?: string;
    appliedDiscountInfo?: Record<string, any>;
  },
): ApplyRequest => ({
  loyaltyInput: {
    discountCode: offer?.discountCode,
    couponCode: appliedDiscountInfo?.couponCode || offer?.couponCode,
  },
  providerId,
  metaId,
  sale,
});

/**
 * What to do with a partner's apply response.
 *
 * The profile decides three things, and the old component had each of them spelled as a
 * different predicate:
 *
 * | question | profile field | was |
 * |---|---|---|
 * | is there a second leg at all? | `requiresOtpOnApply` | `isExternalProviderC1C2(metaId)` |
 * | may this offer waive it? | `honoursOfferOtpFlag` | `isExternalProviderC1(...) && !data.isOtpRequired` |
 * | is a `referenceId` on this leg? | `referenceIdSource === "apply"` | `isExternalProviderC1(response.provider)` |
 *
 * ⚠️ **The `referenceId` is read from the response only when the profile says this leg carries
 * one.** Reading it unconditionally stored `undefined` over a good id for a partner that issues
 * it on VERIFY, which left the removal unblock with nothing to quote.
 *
 * ⚠️ A response with neither a `redemptionInfo` nor a `sale` is a FAILURE, not a silent success.
 * The old code fell through both `if`s and did nothing at all — no discount, no message.
 */
export const getApplyPlan = (
  response: any,
  offer: DiscountOffer | null | undefined,
  metaId?: string,
): ApplyPlan => {
  const code = response?.code || response?.errorCode;
  if (code) return { kind: "failed", code };

  const profile = getDiscountProviderProfile(metaId);
  const redemptionInfo = response?.redemptionInfo;
  const referenceId =
    profile.referenceIdSource === "apply" ? redemptionInfo?.data?.referenceId : undefined;

  // The partner answered a challenge — unless this particular offer waives it and the partner
  // lets an offer do that.
  if (redemptionInfo && profile.requiresOtpOnApply) {
    const waived =
      profile.honoursOfferOtpFlag &&
      redemptionInfo?.success &&
      offer?.isOtpRequired === false;
    return waived
      ? { kind: "apply-now", sale: response?.sale, referenceId }
      : { kind: "needs-otp", sale: response?.sale, referenceId };
  }

  if (response?.sale) return { kind: "apply-now", sale: response.sale, referenceId };
  return { kind: "failed" };
};

/* ── verifying ─────────────────────────────────────────────────────────────────────────── */

/** A verify body, in whichever of the two shapes the partner speaks. */
export type VerifyRequest =
  | {
      loyaltyInput: { discountCode?: string; couponCode?: string; referenceId?: string; otp: string };
      providerId?: string;
      metaId?: string;
      sale: Sale;
    }
  | {
      discountCode?: string;
      couponCode?: string;
      otp: string;
      providerId?: string;
      metaId?: string;
      sale: Sale;
    };

/**
 * The verify request and the action to dispatch it with.
 *
 * 🔴 **The shape and the channel are decided together, and that pairing is the whole reason this
 * function exists.** A nested body sent to the offer endpoint, or a flat one sent to the loyalty
 * endpoint, is rejected as malformed — and the old code kept the two facts in one ternary keyed
 * on `isExternalProviderC1`, so a partner that needed one and not the other could not be
 * expressed at all. `bluetokaiProfile` is exactly that partner.
 *
 * ⚠️ The `referenceId` rides along ONLY in the nested shape, matching the old payload exactly.
 * The flat shape's endpoint correlates by coupon code.
 */
export const getVerifyPlan = (
  offer: DiscountOffer,
  otp: string,
  {
    sale,
    providerId,
    metaId,
    referenceId,
    appliedDiscountInfo = {},
  }: {
    sale: Sale;
    providerId?: string;
    metaId?: string;
    referenceId?: string;
    appliedDiscountInfo?: Record<string, any>;
  },
): { channel: "loyalty-redeem" | "offer-verify"; body: VerifyRequest } => {
  const profile = getDiscountProviderProfile(metaId);
  const couponCode = offer?.couponCode || appliedDiscountInfo?.couponCode;

  const body: VerifyRequest = profile.wrapsVerifyInLoyaltyInput
    ? {
        loyaltyInput: { discountCode: offer?.discountCode, couponCode, referenceId, otp },
        providerId,
        metaId,
        sale,
      }
    : { discountCode: offer?.discountCode, couponCode, otp, providerId, metaId, sale };

  return { channel: profile.verifyChannel, body };
};

/**
 * What a partner's verify response means.
 *
 * The success signal is per partner — `redemptionInfo.success` on the loyalty-shaped channel,
 * `message === "success"` on the offer-shaped one — and the old code tested BOTH and then
 * additionally raised two different toasts guarded by two different predicates. That is the same
 * rule read three times; here it is read once.
 *
 * ⚠️ `itemDiscounts` is `[]` for a partner that returns none, so the caller never has to ask
 * whether the field means "none" or "not applicable" — `shouldApplyItemLevelDiscounts` decides
 * that from the profile.
 *
 * ⚠️ A `code` on the response is surfaced separately from `message`: the backend's error codes
 * map to POS messages through `getErrorMessage`, and the partner's `message` is prose.
 *
 * ⚠️ The coupon RESERVATION is two fields at two depths: `blockedUntil` under
 * `redemptionInfo.data`, `blockId` at the top level. Both go onto the bill entry together.
 */
export const classifyVerifyResponse = (response: any, metaId?: string): VerifyOutcome => {
  const profile = getDiscountProviderProfile(metaId);
  const redemptionInfo = response?.redemptionInfo;

  const succeeded =
    profile.verifySuccessSignal === "redemption-info"
      ? !!redemptionInfo?.success
      : response?.message === "success";

  if (!succeeded) {
    return { kind: "failed", message: response?.message, code: response?.code };
  }

  return {
    kind: "verified",
    sale: response?.sale,
    referenceId:
      profile.referenceIdSource === "verify" ? redemptionInfo?.data?.referenceId : undefined,
    blockedUntil: redemptionInfo?.data?.blockedUntil || undefined,
    // 🔴 `response.blockId`, NOT `redemptionInfo.data.blockId`. The two reservation fields
    // arrive at DIFFERENT depths — `blockedUntil` nested, `blockId` alongside `sale` — so
    // reading this one where its companion lives yields `undefined` on every response.
    blockId: response?.blockId || undefined,
    itemDiscounts: profile.returnsItemLevelDiscounts ? (redemptionInfo?.discounts ?? []) : [],
  };
};

/**
 * A CLONE of the partner's sale with the customer marked as verified BY THE PARTNER.
 *
 * `extAuthenticated` / `extAuthenticatedBy` are copied from the sale's own `authenticated` /
 * `authenticatedBy`, which the partner set when it accepted the OTP. Downstream authentication
 * gates read the `ext*` pair.
 *
 * ⚠️ `cloneDeep`, not `structuredClone` — Hermes has none. See `billDiscount.ts`.
 *
 * 🔴 **Skipped for a partner whose OTP verifies the OFFER rather than the customer** — see
 * `stampsExternalAuthentication`. Setting it there satisfies a customer-verification gate nobody
 * passed. Returns the sale untouched in that case, so the caller needs no branch.
 */
export const stampExternalAuthentication = (sale: Sale, metaId?: string): Sale => {
  if (!getDiscountProviderProfile(metaId).stampsExternalAuthentication) return sale;
  const next: any = cloneDeep(sale);
  if (!next?.customerInfo) return next;
  next.customerInfo.extAuthenticated = next.customerInfo.authenticated;
  next.customerInfo.extAuthenticatedBy = next.customerInfo.authenticatedBy;
  return next;
};

/** Are there per-item discounts to stamp onto the sale, and does this partner send them? */
export const shouldApplyItemLevelDiscounts = (
  itemDiscounts: any[] | undefined,
  metaId?: string,
): boolean =>
  !!itemDiscounts?.length && getDiscountProviderProfile(metaId).returnsItemLevelDiscounts;

/* ── removing ──────────────────────────────────────────────────────────────────────────── */

/** Everything the unblock call needs, and whether it has it. */
export interface UnblockRequest {
  referenceId?: string;
  providerId?: string;
  metaId?: string;
  couponCode?: string;
  customerPhone?: string;
  /**
   * The bill the coupon comes off, sent whole (web `removeExternalDiscount`, `326a931a4`): the
   * partner prices the release against it. Not part of any profile's completeness check.
   */
  sale?: unknown;
}

/**
 * Whether this partner's coupon can be released, and with what.
 *
 * `{ ok: false, missing }` means the partner requires a field we do not have, so the call is
 * skipped and the caller reports `missing` — sending it anyway produces a rejection the cashier
 * cannot act on. `{ ok: true }` sends whatever is present, which is what a partner with looser
 * requirements accepts.
 *
 * Replaces the inline guard in `removeExternalDiscount`, which hard-coded the requirement to
 * class 1 and phrased a missing phone number differently from every other missing field.
 */
export const planCouponUnblock = (
  request: UnblockRequest,
): { ok: true; body: UnblockRequest } | { ok: false; missing: "customerPhone" | "other" } => {
  const profile = getDiscountProviderProfile(request.metaId);
  if (profile.unblockRequiresReferenceAndPhone) {
    if (!request.customerPhone) return { ok: false, missing: "customerPhone" };
    if (!request.referenceId || !request.providerId || !request.couponCode) {
      return { ok: false, missing: "other" };
    }
  }
  return { ok: true, body: request };
};

/**
 * The `referenceId` to STORE after this leg, or `undefined` to leave the stored one alone.
 *
 * One rule for both legs, so a partner's `referenceIdSource` is honoured in one place. Both
 * `getApplyPlan` and `classifyVerifyResponse` already null out the id on the leg that does not
 * carry it, so this is simply "did that leg produce one".
 */
export const getReferenceIdToStore = (
  outcome: { referenceId?: string } | null | undefined,
): string | undefined => outcome?.referenceId || undefined;
